/**
 * @fileoverview Signup API Service
 * @description API calls for the signup flow with Plaid integration
 */
import { apiFetch } from '../lib/api';
import { buildApiError } from '../lib/apiError';

/**
 * Tenant/Business information for signup
 * Contains all organization details
 */
export interface TenantInfo {
  /** Business/Company name (required) */
  name: string;
  /** URL-friendly slug (optional - auto-generated if not provided) */
  slug?: string;
  /** Employer Identification Number (XX-XXXXXXX format) */
  ein?: string;
  /** Street address */
  address?: string;
  /** Address line 2 - Apt/Suite/Unit */
  addressLine2?: string;
  /** City */
  city?: string;
  /** State/Province */
  state?: string;
  /** ZIP/Postal code */
  zipCode?: string;
  /** Country code (ISO 3166-1 alpha-2, default: US) */
  country?: string;
  /** Business phone number */
  phoneNumber?: string;
  /** Company website URL */
  website?: string;
  /** Account manager name */
  managerName?: string;
  /** Account manager email */
  managerEmail?: string;
}

// =============================================================================
// SIGNUP DTOs
// =============================================================================

/**
 * DTO for completing signup
 *
 * User information and tenant information are clearly separated:
 * - User fields: Personal info for the user account
 * - Tenant fields: Business/organization info for the tenant
 */
export interface CompleteSignupDto {
  // Invite Token
  token: string;

  // User Information
  firstName: string;
  lastName: string;
  email: string;
  userPhoneNumber?: string; // User's personal phone

  password: string;

  // Tenant Information (optional for tenant-specific invites)
  tenant?: TenantInfo;
}

/**
 * DTO for exchanging Plaid token
 */
export interface ExchangePlaidTokenDto {
  publicToken: string;
  tenantId: string;
  institutionId?: string;
  institutionName?: string;
  linkSessionId?: string;
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/**
 * Response from complete signup endpoint
 */
export interface SignupResult {
  userId: string;
  /** Tenant ID - null for standalone invites where user has no tenant assignment */
  tenantId: string | null;
  accessToken: string;
  refreshToken: string;
}

/**
 * Response from create link token endpoint
 */
export interface LinkTokenResult {
  linkToken: string;
  expiration: string;
}

/**
 * Error response from API
 */
interface ApiErrorResponse {
  message: string | string[];
  statusCode: number;
  error?: string;
}

/**
 * Custom error class for API errors
 */
export class SignupApiError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'SignupApiError';
    this.statusCode = statusCode;
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Helper to handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  // Handle response errors
  if (!response.ok) {
    // Delegate the wording to the shared normalizer so signup matches the
    // rest of the app. Previously an unparseable body fell back to
    // response.statusText — which is the empty string over HTTP/2, leaving
    // the user with a blank error.
    const { message } = buildApiError(response.status, await response.text());
    throw new SignupApiError(message, response.status);
  }

  // Handle no content responses
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// =============================================================================
// SIGNUP API SERVICE
// =============================================================================

/**
 * Signup API service
 *
 * Provides methods for:
 * - Completing signup (public)
 * - Creating Plaid link token (authenticated)
 * - Exchanging Plaid token (authenticated)
 */
export const signupApi = {
  /**
   * Complete the signup process
   *
   * Creates user profile, tenant, and membership atomically.
   * Returns session tokens for immediate login.
   *
   * @param dto - Signup data including invite token, user info, and tenant info
   * @returns SignupResult with user ID, tenant ID, and session tokens
   */
  complete: async (dto: CompleteSignupDto): Promise<SignupResult> => {
    const response = await apiFetch('/api/signup/complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    return handleResponse<SignupResult>(response);
  },

  /**
   * Create a Plaid link token for bank connection
   *
   * Requires authentication token from signup result.
   *
   * @param tenantId - Tenant ID to associate link with
   * @param accessToken - JWT access token from signup
   * @returns Link token for Plaid Link initialization
   */
  createLinkToken: async (
    tenantId: string,
    accessToken: string,
  ): Promise<LinkTokenResult> => {
    const response = await apiFetch('/api/signup/create-link-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tenantId }),
    }, accessToken);
    return handleResponse<LinkTokenResult>(response);
  },

  /**
   * Exchange Plaid public token for access token
   *
   * Called after user completes Plaid Link.
   * Stores access token securely in Supabase Vault.
   *
   * @param dto - Exchange data including public token and institution info
   * @param accessToken - JWT access token from signup
   */
  exchangeToken: async (
    dto: ExchangePlaidTokenDto,
    accessToken: string,
  ): Promise<void> => {
    const response = await apiFetch('/api/signup/exchange-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    }, accessToken);
    return handleResponse<void>(response);
  },
};

export default signupApi;
